<template>
  <div class="cover-image-setting-mask" @click.self="closeDialog">
    <div
      ref="dialogRoot"
      class="cover-image-setting-dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <header class="cover-image-setting-dialog__header">
        <div class="cover-image-setting-dialog__header-spacer" aria-hidden="true" />
        <h2 :id="titleId" class="cover-image-setting-dialog__title">
          {{ $t("novelDetail.coverImageSetting.title") }}
        </h2>
        <button
          type="button"
          class="cover-image-setting-dialog__close"
          :disabled="isSaving"
          :aria-label="String($t('common.close'))"
          @click="closeDialog"
        >
          <IconClose />
        </button>
      </header>

      <template v-if="step === 'select'">
        <p class="cover-image-setting-dialog__subtitle">{{ $t("novelDetail.coverImageSetting.subtitle") }}</p>

        <div class="cover-image-setting-dialog__grid">
          <button type="button" class="cover-image-setting-card" :disabled="isSaving" @click="onClickUpload">
            <span class="cover-image-setting-card__icon-wrap" aria-hidden="true">
              <IconImagePlus />
            </span>
            <span class="cover-image-setting-card__label">{{ $t("novelDetail.coverImageSetting.upload.title") }}</span>
            <span class="cover-image-setting-card__desc">
              {{ $t("novelDetail.coverImageSetting.upload.description") }}
            </span>
          </button>
          <button type="button" class="cover-image-setting-card" :disabled="isSaving" @click="onClickPreset">
            <span class="cover-image-setting-card__icon-wrap" aria-hidden="true">
              <IconLayoutGrid />
            </span>
            <span class="cover-image-setting-card__label">{{ $t("novelDetail.coverImageSetting.preset.title") }}</span>
            <span class="cover-image-setting-card__desc">
              {{ $t("novelDetail.coverImageSetting.preset.description") }}
            </span>
          </button>
        </div>
      </template>

      <template v-else>
        <div class="cover-image-crop-preview">
          <canvas
            ref="canvasRef"
            class="cover-image-crop-canvas"
            width="1000"
            height="1000"
            @pointerdown="onCanvasPointerDown"
            @pointerup="onCanvasPointerUp"
            @pointercancel="onCanvasPointerUp"
            @pointermove="onCanvasPointerMove"
            @wheel.prevent="onCanvasWheel"
          />
        </div>

        <div class="cover-image-crop-zoom">
          <IconZoomOut class="cover-image-crop-zoom__icon" aria-hidden="true" />
          <input
            type="range"
            class="cover-image-crop-zoom__slider"
            min="0"
            max="100"
            step="1"
            :value="Math.round(zoomT * 100)"
            :style="{
              background: `linear-gradient(to right, var(--color-primary) ${zoomT * 100}%, var(--color-border) ${
                zoomT * 100
              }%)`,
            }"
            :disabled="isSaving"
            :aria-label="String($t('novelDetail.coverImageSetting.crop.zoomLabel'))"
            @input="onZoomInput"
          />
          <IconZoomIn class="cover-image-crop-zoom__icon" aria-hidden="true" />
        </div>

        <button type="button" class="cover-image-crop-confirm-button" :disabled="isSaving" @click="onClickConfirmCrop">
          {{ $t("novelDetail.coverImageSetting.crop.confirmButton") }}
        </button>
        <button type="button" class="cover-image-crop-cancel-button" :disabled="isSaving" @click="onClickCancelCrop">
          {{ $t("common.cancel") }}
        </button>
      </template>

      <input
        ref="fileInputRef"
        type="file"
        accept="image/*"
        class="cover-image-setting-dialog__file-input"
        @change="onFileChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-undef -- defineProps is a Vue compiler macro */
import { getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { setCanvas, submitImage } from "@/lib/image-save";
import { deleteBinary } from "@/lib/s3";
import { openPresetSelectorDialog } from "@/lib/dialog";
import {
  computeFrame,
  computeMinScale,
  computeScaleForT,
  computeTForScale,
  clampImageCenter,
  zoomAtPoint,
  type Range,
  type Point,
} from "@/lib/utils/coverImageCrop";
import IconClose from "~icons/figma/Close";
import IconImagePlus from "~icons/figma/ImagePlus";
import IconLayoutGrid from "~icons/figma/LayoutGrid";
import IconZoomIn from "~icons/figma/ZoomIn";
import IconZoomOut from "~icons/figma/ZoomOut";

// 表紙画像の縦横比は ImageSelector.vue を踏襲する
const COVER_IMAGE_RATIO = 148.0 / 105.0;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
// ズームの最大拡大率(minScaleに対する倍率)。クロップ枠(frame)のサイズは常に固定のため、
// 出力解像度は常にframeのピクセルサイズで決まり、旧実装にあったような枠縮小に伴う解像度低下対策は不要
const MAX_ZOOM_MULTIPLIER = 3;
// マウスホイール1回あたりのズーム量(minScale〜maxScaleの範囲に対する比率)
const WHEEL_ZOOM_STEP = 0.08;

const props = defineProps<{
  novelId: string;
}>();

const titleId = "cover-image-setting-title";

const instance = getCurrentInstance();
const store = instance?.proxy?.$store;

const dialogRoot = ref<HTMLElement | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const canvasRef = ref<HTMLCanvasElement | null>(null);
const isSaving = ref(false);

const step = ref<"select" | "crop">("select");
const pickedImage = ref<HTMLImageElement | null>(null);
// クロップ枠。canvasサイズと比率のみで決まり、ドラッグ・ズーム操作によらず不変
const frame = ref<Range | null>(null);
// 画像がframeを覆いきる最小拡大率(zoomT=0に対応)。画像読み込み時に一度だけ計算する
const minScale = ref(1);
const maxScale = ref(1);
const scale = ref(1);
// 画像中心のcanvas座標。ドラッグ・ズーム操作のたびに更新する
const imageCenter = ref<Point>({ x: 0, y: 0 });
const zoomT = ref(0);

// ダイアログ破棄後にグローバル状態(setCanvas)が古いcanvasを参照し続けないようにする
onBeforeUnmount(() => setCanvas(null));

function closeDialog() {
  if (isSaving.value) return;
  instance?.proxy?.$close(undefined);
}

function onKeydown(e: KeyboardEvent) {
  if (e.key !== "Escape") return;
  closeDialog();
}

onMounted(() => {
  nextTick(() => dialogRoot.value?.focus());
  document.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => document.removeEventListener("keydown", onKeydown));

// canvasの表示サイズと実サイズ(1000x1000)の比率を補正して座標を返す
function getMousePos(canvas: HTMLCanvasElement, evt: { clientX: number; clientY: number }) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;

  return {
    x: (evt.clientX - rect.left) * scaleX,
    y: (evt.clientY - rect.top) * scaleY,
  };
}

// クロップ枠(range)の外側だけモーダル背景と同じ半透明の黒(--color-overlay)を重ねて減光し、
// 選択範囲を強調する。canvasはCSSカラーを描画に使えないため、getComputedStyleで実際のテーマの値を読み取る
// （ライト/ダークで透過率が異なるため固定値にしない）
function getOverlayColor(canvas: HTMLCanvasElement): string {
  const value = getComputedStyle(canvas).getPropertyValue("--color-overlay").trim();
  return value || "rgba(0, 0, 0, 0.64)";
}

function redrawCanvas(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext("2d")!;
  const img = pickedImage.value;
  const range = frame.value;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (img && range) {
    const w = img.width * scale.value;
    const h = img.height * scale.value;
    const dx = imageCenter.value.x - w / 2;
    const dy = imageCenter.value.y - h / 2;
    ctx.drawImage(img, dx, dy, w, h);
  }

  if (!range) return;

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, canvas.width, canvas.height);
  ctx.rect(range.l, range.t, range.r - range.l, range.b - range.t);
  ctx.clip("evenodd");
  ctx.fillStyle = getOverlayColor(canvas);
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.restore();

  // submitImage側でcanvas.width = r - lのように整数化されるため、ここで丸めて
  // 表示上のクリップ境界と実際に切り出されるサイズのズレ(1px程度の暗い縁)を防ぐ
  // eslint-disable-next-line no-param-reassign
  canvas.dataset.clip = [range.l, range.t, range.r, range.b].map((n) => Math.round(n)).join(",");
}

let dragOrigin: { pointerId: number; x: number; y: number; center: Point } | null = null;

// pointer captureを使い、ドラッグ中にカーソルがcanvas外(ダイアログ背景側)に出ても
// ドラッグを継続できるようにする。captureしないと、ドラッグを離した際のpointerupが
// canvasではなく背景マスク側で発生し、マスクの@click.self="closeDialog"により
// 誤ってダイアログごと閉じてしまう事象があったため
// dragOriginをpointerIdで紐付けることで、マルチタッチで2本目の指が触れても
// 進行中のドラッグの基準点が上書きされないようにする
function onCanvasPointerDown(e: PointerEvent) {
  const canvas = e.target as HTMLCanvasElement;
  if (isSaving.value || !frame.value || dragOrigin) return;

  canvas.setPointerCapture(e.pointerId);
  canvas.style.cursor = "grabbing";
  const { x, y } = getMousePos(canvas, e);
  dragOrigin = { pointerId: e.pointerId, x, y, center: imageCenter.value };
}

// ドラッグはframe(枠)ではなく画像側を動かす。frameは常に固定位置のまま
function onCanvasPointerMove(e: PointerEvent) {
  if (!dragOrigin || dragOrigin.pointerId !== e.pointerId || isSaving.value) return;
  const canvas = e.target as HTMLCanvasElement;
  const img = pickedImage.value;
  const range = frame.value;
  if (!img || !range) return;

  const { x, y } = getMousePos(canvas, e);
  const dx = x - dragOrigin.x;
  const dy = y - dragOrigin.y;
  const center = { x: dragOrigin.center.x + dx, y: dragOrigin.center.y + dy };

  imageCenter.value = clampImageCenter(center, scale.value, img.width, img.height, range);
  redrawCanvas(canvas);
}

function onCanvasPointerUp(e: PointerEvent) {
  if (dragOrigin && dragOrigin.pointerId !== e.pointerId) return;
  const canvas = e.target as HTMLCanvasElement;
  // dragOriginが無い(pointerdown側でcaptureされていない)状態でreleasePointerCaptureを呼ぶと
  // NotFoundErrorが投げられるため、captureされている場合のみ呼び出す
  if (canvas.hasPointerCapture(e.pointerId)) {
    canvas.releasePointerCapture(e.pointerId);
  }
  canvas.style.cursor = "move";
  dragOrigin = null;
}

// ズームスライダー操作: frame中心を固定点として画像の拡大率のみを変更する
function onZoomInput(e: Event) {
  const canvas = canvasRef.value;
  const img = pickedImage.value;
  const range = frame.value;
  if (!canvas || !img || !range || isSaving.value) return;

  zoomT.value = Number((e.target as HTMLInputElement).value) / 100;
  const newScale = computeScaleForT(minScale.value, maxScale.value, zoomT.value);
  const anchor = { x: (range.l + range.r) / 2, y: (range.t + range.b) / 2 };

  imageCenter.value = zoomAtPoint(imageCenter.value, scale.value, newScale, anchor, img.width, img.height, range);
  scale.value = newScale;
  redrawCanvas(canvas);
}

// マウスホイール操作: カーソル位置を固定点として画像の拡大率を変更する。ページのスクロールを防ぐため
// テンプレート側で@wheel.prevent="onCanvasWheel"としてpreventDefaultする
function onCanvasWheel(e: WheelEvent) {
  const canvas = canvasRef.value;
  const img = pickedImage.value;
  const range = frame.value;
  if (!canvas || !img || !range || isSaving.value || e.deltaY === 0) return;

  const direction = e.deltaY > 0 ? -1 : 1;
  const zoomStep = direction * WHEEL_ZOOM_STEP * (maxScale.value - minScale.value);
  const newScale = Math.min(maxScale.value, Math.max(minScale.value, scale.value + zoomStep));
  const anchor = getMousePos(canvas, e);

  imageCenter.value = zoomAtPoint(imageCenter.value, scale.value, newScale, anchor, img.width, img.height, range);
  scale.value = newScale;
  zoomT.value = computeTForScale(minScale.value, maxScale.value, newScale);
  redrawCanvas(canvas);
}

// クロップ枠(frame)はcanvasサイズと比率(COVER_IMAGE_RATIO)のみで決まる固定値。画像は初期状態で
// frameにちょうどフィットする拡大率(minScale)・frame中心の位置で配置し、以後はドラッグ・ズーム操作で
// scale/imageCenterのみを変更する(frame自体は動かさない)
function loadImageToCanvas(canvas: HTMLCanvasElement, img: HTMLImageElement) {
  pickedImage.value = img;

  const f = computeFrame({ w: canvas.width, h: canvas.height }, COVER_IMAGE_RATIO);
  frame.value = f;

  const min = computeMinScale(f, img.width, img.height);
  minScale.value = min;
  maxScale.value = min * MAX_ZOOM_MULTIPLIER;
  scale.value = min;
  imageCenter.value = { x: (f.l + f.r) / 2, y: (f.t + f.b) / 2 };
  zoomT.value = 0;

  redrawCanvas(canvas);
  canvas.classList.add("local");
}

// novelModule/updateNovel で image を保存する共通処理
// （NovelDetail.vue のタイトル・あらすじ自動保存と同様、通信エラー等は GraphQLClient 側で
// 既にエラーダイアログの表示・ログ送信が行われているため、ここでは重複させない。
// store 内の novel 未検出は GraphQLClient を経由しないため、dispatch前にチェックしてフォールバックのトーストを出す）
async function saveCoverImage(image: string, closeValue: "upload" | "preset") {
  isSaving.value = true;
  try {
    const novel = store?.getters["novelModule/novel"](props.novelId);
    if (!novel) {
      instance?.proxy?.$notify({
        title: String(instance?.proxy?.$t("novelDetail.coverImageSetting.saveFailed")),
        type: "error",
      });
      return;
    }

    const updated = await store?.dispatch("novelModule/updateNovel", {
      novelId: props.novelId,
      image,
    });
    if (!updated) {
      // GraphQLがエラーを含む場合等、実際には保存されていないため成功扱いにしない
      // （エラー内容はGraphQLClient側の共通ダイアログで既に表示されている）
      return;
    }

    if (closeValue === "preset" && novel.image && (novel.image as string).startsWith("file:")) {
      // アップロード済み表紙からプリセットに切り替えた場合、参照が外れて孤立するS3側の実体も削除する
      // （NovelDetail.vueの表紙削除フローと同様、DB更新は既に成功しているため失敗しても処理は継続する）
      try {
        await deleteBinary(`novels/${props.novelId}/cover.jpg`);
      } catch (error) {
        // 失敗してもDB更新は成功しているため処理は継続する
      }
    }

    instance?.proxy?.$notify({
      title: String(instance?.proxy?.$t("common.saved")),
      type: "success",
      duration: 2000,
    });
    instance?.proxy?.$close(closeValue);
  } catch (error) {
    // 通信エラー等は updateNovelの呼び出し元であるGraphQLClient側で
    // 既にエラーダイアログの表示・ログ送信が行われているため、ここでは重複させない
  } finally {
    isSaving.value = false;
  }
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target?.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// MIME検証(image/*)は通過するがブラウザがデコードできない形式（HEIC等）を、
// 通信失敗等の一般的なアップロード失敗と区別して案内するためのエラー型
class ImageDecodeError extends Error {}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new ImageDecodeError("failed to decode image"));
    img.src = src;
  });
}

function onClickUpload() {
  if (isSaving.value) return;
  fileInputRef.value?.click();
}

// ファイル選択後は即保存せず、クロップ枠を調整できるクロップ画面(step="crop")に遷移する
async function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || isSaving.value) {
    return;
  }

  if (!file.type.startsWith("image/")) {
    instance?.proxy?.$notify({
      title: String(instance?.proxy?.$t("novelDetail.coverImageSetting.uploadFailed")),
      type: "error",
    });
    input.value = "";
    return;
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    instance?.proxy?.$notify({
      title: String(instance?.proxy?.$t("novelDetail.coverImageSetting.fileTooLarge")),
      type: "error",
    });
    input.value = "";
    return;
  }

  isSaving.value = true;
  try {
    const dataUrl = await readFileAsDataUrl(file);
    const img = await loadImage(dataUrl);

    // canvasはstep==="crop"のときしかDOMに存在しない(v-else配下)ため、
    // 先にstepを切り替えてから$nextTickでDOM更新を待ってcanvasRefを取得する
    step.value = "crop";
    await instance?.proxy?.$nextTick();
    const canvas = canvasRef.value;
    if (!canvas) {
      throw new Error("cover image crop canvas not found");
    }
    loadImageToCanvas(canvas, img);
  } catch (error) {
    // canvas未検出等でstepだけ"crop"に進んでしまっている場合に備え、選択画面に戻す
    step.value = "select";
    const messageKey =
      error instanceof ImageDecodeError
        ? "novelDetail.coverImageSetting.unsupportedFormat"
        : "novelDetail.coverImageSetting.uploadFailed";
    instance?.proxy?.$notify({
      title: String(instance?.proxy?.$t(messageKey)),
      type: "error",
    });
  } finally {
    isSaving.value = false;
    // eslint-disable-next-line no-param-reassign
    input.value = "";
  }
}

// PresetSelectorDialog.vue の selected()/cancel() と同様、選択時は { key } を持つオブジェクト、
// キャンセル時は false が解決される（ImageSelector.vue の openPresetDialog を踏襲）
async function onClickPreset() {
  if (isSaving.value) return;

  const response = await openPresetSelectorDialog();
  if (!response) return;

  await saveCoverImage(`preset:${response.key}`, "preset");
}

// クロップ画面のキャンセルは、選択済みの画像を破棄してカード選択画面に戻るだけ(ダイアログ自体は閉じない)
function onClickCancelCrop() {
  if (isSaving.value) return;
  step.value = "select";
  pickedImage.value = null;
  frame.value = null;
  minScale.value = 1;
  maxScale.value = 1;
  scale.value = 1;
  imageCenter.value = { x: 0, y: 0 };
  zoomT.value = 0;
}

// 「設定」ボタン: クロップ枠(canvas.dataset.clip)で確定した範囲を submitImage() が切り出してアップロードする
// 注意: S3への保存(submitImage)とDBの更新(saveCoverImage)は別々の通信のため、ロールバック機構は無い。
// submitImage成功後にsaveCoverImageが失敗した場合、S3側は既に新しい画像に置き換わっているため、
// エラー表示後に再読み込みすると新しい表紙が反映された状態になり得る（仕様として許容している）
async function onClickConfirmCrop() {
  const canvas = canvasRef.value;
  if (isSaving.value || !canvas) return;

  isSaving.value = true;
  try {
    setCanvas(canvas);
    // 正規形（backendの移行スクリプトが生成する形式）に合わせる
    const uploaded = await submitImage(`novels/${props.novelId}/cover.jpg`);
    if (!uploaded) {
      instance?.proxy?.$notify({
        title: String(instance?.proxy?.$t("novelDetail.coverImageSetting.uploadFailed")),
        type: "error",
      });
      return;
    }
    await saveCoverImage(`file:novels/${props.novelId}/cover.jpg`, "upload");
  } catch (error) {
    instance?.proxy?.$notify({
      title: String(instance?.proxy?.$t("novelDetail.coverImageSetting.uploadFailed")),
      type: "error",
    });
  } finally {
    isSaving.value = false;
  }
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.cover-image-setting-mask {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.cover-image-setting-dialog {
  width: 100%;
  max-width: 400px;
  margin: 0 16px;
  padding: 20px 20px 24px;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
}

.cover-image-setting-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.cover-image-setting-dialog__header-spacer {
  width: 28px;
  flex-shrink: 0;
}

.cover-image-setting-dialog__title {
  flex: 1;
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  text-align: center;
}

.cover-image-setting-dialog__close {
  position: relative;
  @include hover-overlay;
  display: flex;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-normal);
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  ::v-deep svg {
    width: 14px;
    height: 14px;
  }
}

.cover-image-setting-dialog__subtitle {
  margin: 0 0 20px;
  font-size: var(--font-size-xs);
  line-height: var(--line-height-default);
  color: var(--color-text-low);
  text-align: center;
}

.cover-image-setting-dialog__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.cover-image-setting-card {
  position: relative;
  display: flex;
  min-height: 140px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  border: none;
  border-radius: var(--radius-md);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  text-align: center;
  cursor: pointer;
  @include hover-overlay;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.cover-image-setting-dialog__file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.cover-image-setting-card__icon-wrap {
  display: flex;
  margin-bottom: 10px;
  align-items: center;
  justify-content: center;
  font-size: 32px;
}

.cover-image-setting-card__label {
  margin-bottom: 6px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
}

.cover-image-setting-card__desc {
  font-size: var(--font-size-xs);
  line-height: var(--line-height-default);
  color: var(--color-text-low);
}

.cover-image-crop-preview {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  margin: 0 auto 16px;
  background-color: var(--color-surface-muted);
  overflow: hidden;
}

.cover-image-crop-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: move;
  touch-action: none;
}

.cover-image-crop-zoom {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 16px 28px;
}

.cover-image-crop-zoom__icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;

  ::v-deep svg {
    width: 16px;
    height: 16px;
  }
}

.cover-image-crop-zoom__slider {
  flex: 1;
  height: 4px;
  appearance: none;
  background: var(--color-border);
  border-radius: var(--radius-xs);
  outline: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: var(--radius-circle);
    background: var(--color-background-normal);
    border: 1px solid var(--color-border);
    box-shadow: var(--shadow-sm);
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-circle);
    background: var(--color-background-normal);
    box-shadow: var(--shadow-sm);
    cursor: pointer;
  }
}

.cover-image-crop-confirm-button {
  position: relative;
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  @include hover-overlay;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.cover-image-crop-cancel-button {
  position: relative;
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  @include hover-overlay;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
